import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import EmptyState from '../components/EmptyState';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { formatDate } from '../lib/utils';

export default function LogsPage() {
  const { t, lang } = useLang();
  const locale = lang === 'es' ? 'es' : 'en-US';
  const [correlationId, setCorrelationId] = useState('');
  const [appliedCid, setAppliedCid] = useState('');

  const logs = useQuery({
    queryKey: ['logs', 'sync', appliedCid],
    queryFn: async () => (await api.get('/logs', {
      params: appliedCid ? { correlation_id: appliedCid } : {},
    })).data,
  });
  const alerts = useQuery({
    queryKey: ['logs', 'alerts'],
    queryFn: async () => (await api.get('/logs/alerts')).data,
  });

  return (
    <div data-testid="logs-page">
      <PageHeader title={t('logs.title')} subtitle={t('logs.subtitle')} />

      <Tabs defaultValue="logs" data-testid="logs-tabs">
        <TabsList>
          <TabsTrigger value="logs">{t('logs.tab_logs')}</TabsTrigger>
          <TabsTrigger value="alerts">{t('logs.tab_alerts')}</TabsTrigger>
        </TabsList>

        <TabsContent value="logs">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <div className="flex-1 min-w-[240px]">
                  <label className="text-xs text-muted-foreground">{t('logs.correlation_id')}</label>
                  <Input
                    value={correlationId}
                    onChange={(e) => setCorrelationId(e.target.value)}
                    placeholder="correlation_id"
                    data-testid="logs-correlation-id-input"
                  />
                </div>
                <Button onClick={() => setAppliedCid(correlationId.trim())}
                  size="sm" data-testid="logs-search-button">
                  {t('common.search')}
                </Button>
                {appliedCid && (
                  <Button variant="ghost" size="sm"
                    onClick={() => { setCorrelationId(''); setAppliedCid(''); }}
                    data-testid="logs-clear-button">
                    {t('common.clear')}
                  </Button>
                )}
              </div>
              {logs.isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : (logs.data?.items || []).length === 0 ? (
                <EmptyState message={t('common.empty')} testid="logs-empty" />
              ) : (
                <Table data-testid="logs-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common.date')}</TableHead>
                      <TableHead>{t('logs.level')}</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>{t('logs.correlation_id')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(logs.data?.items || []).map((l, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs whitespace-nowrap">{formatDate(l.created_at, locale)}</TableCell>
                        <TableCell><span className="font-mono text-xs uppercase">{l.level}</span></TableCell>
                        <TableCell className="text-xs">{l.event}</TableCell>
                        <TableCell className="font-mono text-[11px]">{l.correlation_id}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              {alerts.isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
              ) : (alerts.data?.items || []).length === 0 ? (
                <EmptyState message={t('common.empty')} testid="alerts-empty" />
              ) : (
                <Table data-testid="alerts-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common.date')}</TableHead>
                      <TableHead>{t('common.type')}</TableHead>
                      <TableHead>Detail</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(alerts.data?.items || []).map((a, i) => {
                      const typeLabel = t(`alert_type.${a.type}`);
                      return (
                      <TableRow key={i} data-testid={`alert-row-${i}`}>
                        <TableCell className="text-xs whitespace-nowrap">{formatDate(a.created_at, locale)}</TableCell>
                        <TableCell className="text-xs" data-testid={`alert-type-${i}`}>
                          {typeLabel === `alert_type.${a.type}` ? a.type : typeLabel}
                        </TableCell>
                        <TableCell className="text-xs">{a.detail || a.subject || ''}</TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
